import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { RoomList } from "@/components/RoomList";

export const ROOM_CATALOG = [
  { group: "Architecture & Exterior", only: "Architecture", rooms: ["Front Façade", "Rear Elevation", "Entrance", "Courtyard", "Roof Terrace", "Landscape", "Parking", "Staircase Core"] },
  { group: "Living", rooms: ["Living Room", "Family Lounge", "Drawing Room", "Media Room", "Foyer / Entry", "Lobby"] },
  { group: "Sleeping", rooms: ["Master Bedroom", "Bedroom", "Guest Bedroom", "Kids Bedroom", "Nursery", "Walk-in Wardrobe"] },
  { group: "Kitchen & Dining", rooms: ["Kitchen", "Dining", "Breakfast Nook", "Pantry", "Bar"] },
  { group: "Bath", rooms: ["Master Bathroom", "Bathroom", "Powder Room", "Ensuite"] },
  { group: "Work & Leisure", rooms: ["Study", "Home Office", "Library", "Gym", "Home Theatre", "Prayer Room"] },
  { group: "Outdoor & Utility", rooms: ["Balcony", "Terrace", "Garden", "Pool Deck", "Laundry", "Store Room", "Staircase"] },
  { group: "Commercial", rooms: ["Reception", "Open Workspace", "Meeting Room", "Cabin", "Cafeteria", "Retail Floor", "Hotel Suite", "Restaurant Dining"] },
];

const pad = (n) => String(n).padStart(2, "0");

function countOf(rooms, base) {
  return rooms.filter(r => r === base || r.startsWith(`${base} `) && /^\d{2}$/.test(r.slice(base.length + 1))).length;
}

function renumber(rooms, base) {
  const idx = rooms.map((r, i) => (r === base || (r.startsWith(`${base} `) && /^\d{2}$/.test(r.slice(base.length + 1)))) ? i : -1).filter(i => i >= 0);
  if (idx.length <= 1) return rooms.map((r, i) => idx.includes(i) ? base : r);
  const out = [...rooms];
  idx.forEach((i, n) => { out[i] = `${base} ${pad(n + 1)}`; });
  return out;
}

export function RoomPicker({ rooms, onChange, discipline = "Interior" }) {
  const [custom, setCustom] = useState("");

  const inc = (base) => onChange(renumber([...rooms, base], base));
  const dec = (base) => {
    const i = rooms.map((r, k) => (r === base || (r.startsWith(`${base} `) && /^\d{2}$/.test(r.slice(base.length + 1)))) ? k : -1).filter(k => k >= 0).pop();
    if (i === undefined) return;
    onChange(renumber(rooms.filter((_, k) => k !== i), base));
  };
  const remove = (i) => onChange(rooms.filter((_, k) => k !== i));
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    onChange(rooms.includes(v) ? renumber([...rooms, v], v) : [...rooms, v]);
    setCustom("");
  };

  return (
    <div data-testid="room-picker">
      <div className="space-y-5 mb-6">
        {ROOM_CATALOG.filter(g => !g.only || g.only === discipline).map(({ group, rooms: list }) => (
          <div key={group}>
            <div className="eyebrow mb-2">{group}</div>
            <div className="flex flex-wrap gap-2">
              {list.map(base => {
                const n = countOf(rooms, base);
                return (
                  <div key={base} data-testid={`room-${base}`} className={`inline-flex items-center border text-sm transition-colors ${n ? "bg-black text-white border-black" : "bg-white border-stone-300 hover:border-stone-500"}`}>
                    <button type="button" onClick={() => inc(base)} className="px-3 py-2" data-testid={`room-add-${base}`}>{base}</button>
                    {n > 0 && (
                      <div className="flex items-center border-l border-white/30">
                        <button type="button" onClick={() => dec(base)} className="px-2 py-2 hover:bg-white/15" data-testid={`room-dec-${base}`} aria-label={`Remove one ${base}`}><Minus size={12} /></button>
                        <span className="font-mono-lab text-xs w-5 text-center" data-testid={`room-count-${base}`}>{n}</span>
                        <button type="button" onClick={() => inc(base)} className="px-2 py-2 hover:bg-white/15" data-testid={`room-inc-${base}`} aria-label={`Add one ${base}`}><Plus size={12} /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-6">
        <input value={custom} onChange={e => setCustom(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          placeholder="Add a custom space (e.g. Wine Cellar, Puja Room, Mud Room)" className="flex-1 border-b border-stone-300 py-1 bg-transparent outline-none" data-testid="input-custom-room" />
        <button type="button" onClick={addCustom} className="text-sm px-4 border border-stone-300 hover:border-black" data-testid="btn-add-room">Add</button>
      </div>

      <RoomList rooms={rooms} onRename={(i, v) => onChange(rooms.map((x, k) => k === i ? v : x))} onRemove={remove} />
    </div>
  );
}
